import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  EnvelopeIcon,
  ArrowRightIcon,
  KeyIcon,
  CheckCircleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import Logo from '../../components/common/Logo';
import { requestPasswordReset, verifyResetCode, resetPassword } from '../../services/authService';

const ForgotPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // email, verification, newPassword
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue
  } = useForm();

  // Step 1: Request password reset
  const handleRequestReset = async (data) => {
    setLoading(true);
    try {
      await requestPasswordReset(data.email);
      setEmail(data.email);
      setStep('verification');
      toast.success(t('forgotPassword.sendCodeSuccess'));
    } catch (error) {
      console.error('Password reset request error:', error);
      toast.error(t('forgotPassword.sendCodeError'));
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify code
  const handleVerifyCode = async (data) => {
    setLoading(true);
    try {
      await verifyResetCode(email, data.verificationCode);
      setVerificationCode(data.verificationCode);
      setStep('newPassword');
      toast.success(t('forgotPassword.verifyCodeSuccess'));
    } catch (error) {
      console.error('Code verification error:', error);
      toast.error(t('forgotPassword.verifyCodeError'));
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset password
  const handlePasswordReset = async (data) => {
    if (data.newPassword !== data.confirmPassword) {
      toast.error(t('forgotPassword.passwordMismatch'));
      return;
    }

    if (data.newPassword.length < 6) {
      toast.error(t('forgotPassword.passwordMinLengthError'));
      return;
    }

    setLoading(true);
    try {
      await resetPassword(email, verificationCode, data.newPassword);
      toast.success(t('forgotPassword.resetPasswordSuccess'));
      navigate('/login');
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error(t('forgotPassword.resetPasswordError'));
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 'email':
        return (
          <form onSubmit={handleSubmit(handleRequestReset)} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                {t('forgotPassword.emailLabel')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('email', {
                    required: t('forgotPassword.emailRequired'),
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('forgotPassword.emailInvalid'),
                    },
                  })}
                  type="email"
                  className={`block w-full pr-10 pl-3 py-3 border ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  } rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent`}
                  placeholder={t('forgotPassword.emailPlaceholder')}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-white bg-gradient-to-r from-brand-blue to-brand-blueSecondary hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <EnvelopeIcon className="h-5 w-5 ml-2" />
                  {t('forgotPassword.sendCodeButton')}
                </>
              )}
            </motion.button>
          </form>
        );

      case 'verification':
        return (
          <form onSubmit={handleSubmit(handleVerifyCode)} className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <CheckCircleIcon className="w-5 h-5 text-green-600" />
                <p className="text-green-800 text-sm">
                  {t('forgotPassword.codeSentMessage')}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('forgotPassword.verificationCodeLabel')}
              </label>
              <input
                {...register('verificationCode', {
                  required: t('forgotPassword.verificationCodeRequired'),
                  pattern: {
                    value: /^\d{6}$/,
                    message: t('forgotPassword.verificationCodePattern'),
                  },
                })}
                type="text"
                maxLength={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent text-center text-2xl tracking-widest"
                placeholder={t('forgotPassword.verificationCodePlaceholder')}
              />
              {errors.verificationCode && (
                <p className="mt-1 text-sm text-red-600">{errors.verificationCode.message}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {t('forgotPassword.codeInstructions')}
              </p>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setStep('email')}
                className="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all flex items-center gap-2"
              >
                <ArrowRightIcon className="w-4 h-4" />
                {t('common.back')}
              </button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <CheckCircleIcon className="w-5 h-5" />
                    {t('forgotPassword.verifyCodeButton')}
                  </>
                )}
              </motion.button>
            </div>
          </form>
        );

      case 'newPassword':
        return (
          <form onSubmit={handleSubmit(handlePasswordReset)} className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <CheckCircleIcon className="w-5 h-5 text-green-600" />
                <p className="text-green-800 text-sm">
                  {t('forgotPassword.codeVerifiedMessage')}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('forgotPassword.newPasswordLabel')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <KeyIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('newPassword', {
                    required: t('forgotPassword.newPasswordRequired'),
                    minLength: {
                      value: 6,
                      message: t('forgotPassword.newPasswordMinLength'),
                    },
                  })}
                  type="password"
                  className="block w-full pr-10 pl-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                  placeholder={t('forgotPassword.newPasswordPlaceholder')}
                />
              </div>
              {errors.newPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.newPassword.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('forgotPassword.confirmPasswordLabel')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <KeyIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('confirmPassword', {
                    required: t('forgotPassword.confirmPasswordRequired'),
                    validate: (value) =>
                      value === getValues('newPassword') || t('forgotPassword.passwordMismatch'),
                  })}
                  type="password"
                  className="block w-full pr-10 pl-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                  placeholder={t('forgotPassword.confirmPasswordPlaceholder')}
                />
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setStep('verification')}
                className="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all flex items-center gap-2"
              >
                <ArrowRightIcon className="w-4 h-4" />
                {t('common.back')}
              </button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <KeyIcon className="w-5 h-5" />
                    {t('forgotPassword.changePasswordButton')}
                  </>
                )}
              </motion.button>
            </div>
          </form>
        );

      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 'email':
        return t('forgotPassword.emailStepTitle');
      case 'verification':
        return t('forgotPassword.verificationStepTitle');
      case 'newPassword':
        return t('forgotPassword.passwordStepTitle');
      default:
        return '';
    }
  };

  const getStepSubtitle = () => {
    switch (step) {
      case 'email':
        return t('forgotPassword.emailStepSubtitle');
      case 'verification':
        return t('forgotPassword.verificationStepSubtitle');
      case 'newPassword':
        return t('forgotPassword.passwordStepSubtitle');
      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <Logo size="xl" animated={true} />
          </Link>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            {getStepTitle()}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {getStepSubtitle()}
          </p>
        </div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white py-8 px-6 shadow-xl rounded-2xl"
        >
          {renderStepContent()}

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-brand-blue hover:text-brand-blueSecondary flex items-center justify-center gap-2"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              {t('forgotPassword.backToLogin')}
            </Link>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;